import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

/**
 * Maps API URLs to RxDB collection names and extracts query params.
 * Used by the offline interceptor to serve GET requests from local storage.
 */

export interface RouteMapping {
  collection: string;
  selector: Record<string, unknown>;
  idParam?: string;
}

interface RoutePattern {
  urlPattern: RegExp;
  collection: string;
  extractSelector?: (match: RegExpMatchArray, urlParams: URLSearchParams) => Record<string, unknown>;
  extractId?: (match: RegExpMatchArray) => string;
}

const ROUTE_PATTERNS: RoutePattern[] = [
  // ─── Inventory ───
  { urlPattern: /\/products\/(\d+)$/, collection: 'products', extractId: (m) => m[1] },
  { urlPattern: /\/products\/?(\?|$)/, collection: 'products', extractSelector: (_m, p) => {
    const sel: Record<string, unknown> = {};
    if (p.has('isActive')) sel['isActive'] = p.get('isActive') === 'true';
    return sel;
  }},
  { urlPattern: /\/stocks\/(\d+)$/, collection: 'stocks', extractId: (m) => m[1] },
  { urlPattern: /\/stocks\/?(\?|$)/, collection: 'stocks', extractSelector: (_m, p) => {
    const sel: Record<string, unknown> = {};
    if (p.has('warehouseId')) sel['warehouseId'] = Number(p.get('warehouseId'));
    return sel;
  }},
  { urlPattern: /\/warehouses\/(\d+)$/, collection: 'warehouses', extractId: (m) => m[1] },
  { urlPattern: /\/warehouses\/?(\?|$)/, collection: 'warehouses' },
  { urlPattern: /\/stock-movements\/?(\?|$)/, collection: 'stock_movements' },
  // ─── Partners ───
  { urlPattern: /\/customers\/(\d+)$/, collection: 'customers', extractId: (m) => m[1] },
  { urlPattern: /\/customers\/?(\?|$)/, collection: 'customers' },
  { urlPattern: /\/suppliers\/(\d+)$/, collection: 'suppliers', extractId: (m) => m[1] },
  { urlPattern: /\/suppliers\/?(\?|$)/, collection: 'suppliers' },
  { urlPattern: /\/sales-orders\/(\d+)$/, collection: 'sales_orders', extractId: (m) => m[1] },
  { urlPattern: /\/sales-orders\/?(\?|$)/, collection: 'sales_orders' },
  { urlPattern: /\/purchase-orders\/(\d+)$/, collection: 'purchase_orders', extractId: (m) => m[1] },
  { urlPattern: /\/purchase-orders\/?(\?|$)/, collection: 'purchase_orders' },
  { urlPattern: /\/pricing-rules\/?(\?|$)/, collection: 'pricing_rules' },
  { urlPattern: /\/tax-rules\/?(\?|$)/, collection: 'tax_rules' },
  { urlPattern: /\/invoices\/(\d+)$/, collection: 'invoices', extractId: (m) => m[1] },
  { urlPattern: /\/invoices\/?(\?|$)/, collection: 'invoices' },
  { urlPattern: /\/currency-rates\/?(\?|$)/, collection: 'currency_rates' },
  // ─── Accounting ───
  { urlPattern: /\/chart-of-accounts\/?(\?|$)/, collection: 'chart_of_accounts' },
  { urlPattern: /\/journal-entries\/(\d+)$/, collection: 'journal_entries', extractId: (m) => m[1] },
  { urlPattern: /\/journal-entries\/?(\?|$)/, collection: 'journal_entries', extractSelector: (_m, p) => {
    const sel: Record<string, unknown> = {};
    if (p.has('fiscalYearId')) sel['fiscalYearId'] = Number(p.get('fiscalYearId'));
    if (p.has('status')) sel['status'] = p.get('status');
    return sel;
  }},
  { urlPattern: /\/fiscal-years\/?(\?|$)/, collection: 'fiscal_years' },
  { urlPattern: /\/pay-runs\/?(\?|$)/, collection: 'pay_runs' },
  // ─── HR ───
  { urlPattern: /\/employees\/(\d+)$/, collection: 'employees', extractId: (m) => m[1] },
  { urlPattern: /\/employees\/?(\?|$)/, collection: 'employees', extractSelector: (_m, p) => {
    const sel: Record<string, unknown> = {};
    if (p.has('isActive')) sel['isActive'] = p.get('isActive') === 'true';
    return sel;
  }},
  { urlPattern: /\/contracts\/?(\?|$)/, collection: 'contracts' },
  { urlPattern: /\/leave-requests\/?(\?|$)/, collection: 'leave_requests' },
  { urlPattern: /\/departments\/?(\?|$)/, collection: 'departments' },
  // ─── Users ───
  { urlPattern: /\/users\/(\d+)$/, collection: 'users', extractId: (m) => m[1] },
  { urlPattern: /\/users\/?(\?|$)/, collection: 'users' },
  { urlPattern: /\/roles\/?(\?|$)/, collection: 'roles' },
  // ─── POS ───
  { urlPattern: /\/pos\/init/, collection: '__pos_init__' },
  // ─── Reference Data ───
  { urlPattern: /\/ref-data\/product-families\/(\d+)$/, collection: 'product_families', extractId: (m) => m[1] },
  { urlPattern: /\/ref-data\/product-families\/?(\?|$)/, collection: 'product_families' },
  { urlPattern: /\/ref-data\/product-types\/(\d+)$/, collection: 'product_types', extractId: (m) => m[1] },
  { urlPattern: /\/ref-data\/product-types\/?(\?|$)/, collection: 'product_types' },
  { urlPattern: /\/ref-data\/variants\/(\d+)$/, collection: 'variants', extractId: (m) => m[1] },
  { urlPattern: /\/ref-data\/variants\/?(\?|$)/, collection: 'variants' },
  { urlPattern: /\/ref-data\/units\/?(\?|$)/, collection: 'reference_data', extractSelector: () => ({ type: 'units' }) },
  { urlPattern: /\/ref-data\/payment-methods\/?(\?|$)/, collection: 'reference_data', extractSelector: () => ({ type: 'payment-methods' }) },
  { urlPattern: /\/ref-data\/delivery-methods\/?(\?|$)/, collection: 'reference_data', extractSelector: () => ({ type: 'delivery-methods' }) },
  { urlPattern: /\/ref-data\/client-types\/?(\?|$)/, collection: 'reference_data', extractSelector: () => ({ type: 'client-types' }) },
  { urlPattern: /\/ref-data\/warehouse-types\/?(\?|$)/, collection: 'reference_data', extractSelector: () => ({ type: 'warehouse-types' }) },
  { urlPattern: /\/ref-data\/zone-types\/?(\?|$)/, collection: 'reference_data', extractSelector: () => ({ type: 'zone-types' }) },
  { urlPattern: /\/ref-data\/movement-types\/?(\?|$)/, collection: 'reference_data', extractSelector: () => ({ type: 'movement-types' }) },
  { urlPattern: /\/ref-data\/fee-types\/?(\?|$)/, collection: 'reference_data', extractSelector: () => ({ type: 'fee-types' }) },
  { urlPattern: /\/ref-data\/parental-links\/?(\?|$)/, collection: 'reference_data', extractSelector: () => ({ type: 'parental-links' }) },
  // Catch-all for other ref-data types
  { urlPattern: /\/ref-data\/([\w-]+)\/?(\?|$)/, collection: 'reference_data', extractSelector: (m) => ({ type: m[1] }) },
];

@Injectable({ providedIn: 'root' })
export class OfflineRouteMapper {

  resolve(url: string): RouteMapping | null {
    const urlObj = this.parseUrl(url);
    if (!urlObj) return null;

    const path = urlObj.pathname;
    const params = urlObj.searchParams;

    for (const pattern of ROUTE_PATTERNS) {
      const match = path.match(pattern.urlPattern);
      if (match) {
        const mapping: RouteMapping = {
          collection: pattern.collection,
          selector: pattern.extractSelector ? pattern.extractSelector(match, params) : {},
        };
        if (pattern.extractId) {
          mapping.idParam = pattern.extractId(match);
        }
        return mapping;
      }
    }
    return null;
  }

  private parseUrl(url: string): URL | null {
    try {
      if (url.startsWith('http')) {
        return new URL(url);
      }
      return new URL(url, 'http://localhost');
    } catch {
      return null;
    }
  }
}
